import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Scale from 'lucide-react/dist/esm/icons/scale';

const TermsOfUsePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'definitions', title: '2. Definitions' },
    { id: 'company-info', title: '3. Company Information' },
    { id: 'acceptance', title: '4. Acceptance of Terms' },
    { id: 'use-website', title: '5. Use of the Website' },
    { id: 'intellectual-property', title: '6. Intellectual Property Rights' },
    { id: 'information', title: '7. Information on the Website' },
    { id: 'user-contributions', title: '8. User Contributions' },
    { id: 'third-party-links', title: '9. Links to Third Party Websites' },
    { id: 'limitation-liability', title: '10. Limitation of Liability' },
    { id: 'indemnification', title: '11. Indemnification' },
    { id: 'governing-law', title: '12. Governing Law and Jurisdiction' },
    { id: 'dispute-resolution', title: '13. Dispute Resolution' },
    { id: 'termination', title: '14. Termination' },
    { id: 'waiver-severability', title: '15. Waiver and Severability' },
    { id: 'entire-agreement', title: '16. Entire Agreement' },
    { id: 'electronic-communications', title: '17. Electronic Communications' },
    { id: 'assignment', title: '18. Assignment' },
    { id: 'force-majeure', title: '19. Force Majeure' },
    { id: 'notices', title: '20. Notices' },
    { id: 'accessibility', title: '21. Accessibility' },
    { id: 'language', title: '22. Language' },
    { id: 'contact', title: '23. Questions and Contact Information' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <motion.section
        className="bg-gradient-to-br from-[#000565] to-[#0383ED] text-white py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container-custom">
          <motion.div
            className="flex items-center gap-3 mb-4"
            {...fadeInUp}
          >
            <Scale className="w-8 h-8" />
            <FileText className="w-8 h-8" />
          </motion.div>
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-4"
            {...fadeInUp}
          >
            Terms of Use
          </motion.h1>
          <motion.p
            className="text-xl text-blue-100 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Last Updated: 01 November 2025
          </motion.p>
          <motion.p
            className="text-lg text-blue-100 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Please read these terms carefully before using our website
          </motion.p>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <motion.aside
            className="lg:w-1/4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-lg shadow-md p-6 lg:sticky lg:top-24">
              <h2 className="text-lg font-bold text-[#000565] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Table of Contents
              </h2>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left text-sm text-gray-600 hover:text-[#0383ED] hover:pl-2 transition-all duration-200"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </motion.aside>

          {/* Content */}
          <motion.main
            className="lg:w-3/4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
              {/* Section 1: Introduction */}
              <section id="introduction" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">1. INTRODUCTION</h2>
                <p className="text-gray-700 mb-4">
                  Welcome to the website of OMC Chartered Accountants (Pty) Ltd (Registration Number 2008/015646/21), a company duly incorporated in accordance with the laws of the Republic of South Africa.
                </p>
                <p className="text-gray-700 mb-4">
                  These Terms of Use govern your access to and use of our website, including any content, functionality, and services offered on or through www.omcca.co.za (the "Website").
                </p>
                <p className="text-gray-700">
                  By accessing or using the Website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree with these Terms of Use, you must not access or use the Website.
                </p>
              </section>

              {/* Section 2: Definitions */}
              <section id="definitions" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">2. DEFINITIONS</h2>
                <p className="text-gray-700 mb-4">
                  In these Terms of Use, unless the context indicates otherwise:
                </p>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong className="text-[#000565]">"Company", "we", "us", or "our"</strong> means OMC Chartered Accountants (Pty) Ltd, Registration Number 2008/015646/21, with registered address at 3 North Close, Sunninghill Estate, 2191, South Africa.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"User", "you", or "your"</strong> means any person who accesses or uses the Website.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Content"</strong> means all information, text, graphics, images, logos, button icons, software, data compilations, and other material available on the Website.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Services"</strong> means the accounting, auditing, and management consulting services offered by the Company.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Intellectual Property"</strong> means all patents, rights to inventions, copyright and related rights, trademarks, business names, domain names, rights in get-up, goodwill, rights to sue for passing off, rights in designs, database rights, rights to use and protect confidential information, and all other intellectual property rights, in each case whether registered or unregistered.
                  </p>
                </div>
              </section>

              {/* Section 3: Company Information */}
              <section id="company-info" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">3. COMPANY INFORMATION</h2>
                <div className="bg-gray-50 rounded-lg p-6 space-y-2 text-gray-700">
                  <p><strong className="text-[#000565]">Legal Name:</strong> OMC Chartered Accountants (Pty) Ltd</p>
                  <p><strong className="text-[#000565]">Registration Number:</strong> 2008/015646/21</p>
                  <p><strong className="text-[#000565]">Registered Address:</strong> 3 North Close, Sunninghill Estate, 2191, South Africa</p>
                  <p><strong className="text-[#000565]">Email Address:</strong> <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a></p>
                  <p><strong className="text-[#000565]">Telephone Number:</strong> <a href="tel:+27100011234" className="text-[#0383ED] hover:underline">+27 10 001 1234</a></p>
                </div>
              </section>

              {/* Section 4: Acceptance of Terms */}
              <section id="acceptance" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">4. ACCEPTANCE OF TERMS</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using this Website, you accept and agree to be bound by these Terms of Use and all applicable laws and regulations. We reserve the right to modify these Terms of Use at any time without prior notice. Your continued use of the Website following any changes constitutes your acceptance of the modified Terms of Use.
                </p>
                <p className="text-gray-700">
                  It is your responsibility to review these Terms of Use periodically for updates or changes.
                </p>
              </section>

              {/* Section 5: Use of the Website */}
              <section id="use-website" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">5. USE OF THE WEBSITE</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">5.1 Permitted Use</h3>
                <p className="text-gray-700 mb-3">
                  You may access and use the Website for lawful purposes only and in accordance with these Terms of Use. You agree to use the Website only for purposes that are permitted by:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) these Terms of Use;</li>
                  <li>(b) any applicable law, regulation, or generally accepted practices or guidelines in South Africa or any other relevant jurisdiction.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">5.2 Prohibited Use</h3>
                <p className="text-gray-700 mb-3">You agree not to:</p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) use the Website in any way that violates any applicable national or international law or regulation;</li>
                  <li>(b) exploit, harm, or attempt to exploit or harm minors in any way;</li>
                  <li>(c) transmit or procure the sending of any advertising or promotional material, including any "junk mail", "chain letter", "spam", or any other similar solicitation;</li>
                  <li>(d) impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity;</li>
                  <li>(e) engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Website;</li>
                  <li>(f) use the Website in any manner that could disable, overburden, damage, or impair the Website;</li>
                  <li>(g) use any robot, spider, or other automatic device to access the Website;</li>
                  <li>(h) use any manual process to monitor or copy any material on the Website;</li>
                  <li>(i) use any device, software, or routine that interferes with the proper working of the Website;</li>
                  <li>(j) introduce any viruses, trojan horses, worms, logic bombs, or other malicious material;</li>
                  <li>(k) attempt to gain unauthorised access to any parts of the Website;</li>
                  <li>(l) attack the Website via a denial of service attack;</li>
                  <li>(m) otherwise attempt to interfere with the proper working of the Website.</li>
                </ul>
              </section>

              {/* Section 6: Intellectual Property Rights */}
              <section id="intellectual-property" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">6. INTELLECTUAL PROPERTY RIGHTS</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.1 Ownership</h3>
                <p className="text-gray-700 mb-4">
                  The Website and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by the Company, its licensors, or other providers of such material and are protected by South African and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.2 Limited Licence</h3>
                <p className="text-gray-700 mb-3">
                  Subject to your compliance with these Terms of Use, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable licence to access and use the Website for your personal, non-commercial use only. This licence does not include any right to:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) resell or make any commercial use of the Website or its contents;</li>
                  <li>(b) collect and use any product listings, descriptions, or prices;</li>
                  <li>(c) make any derivative use of the Website or its contents;</li>
                  <li>(d) download or copy account information for the benefit of another party;</li>
                  <li>(e) use any data mining, robots, or similar data gathering and extraction tools.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.3 Restrictions</h3>
                <p className="text-gray-700 mb-3">
                  You must not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Website, except as follows:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials;</li>
                  <li>(b) you may store files that are automatically cached by your web browser for display enhancement purposes;</li>
                  <li>(c) you may print or download one copy of a reasonable number of pages of the Website for your own personal, non-commercial use;</li>
                  <li>(d) if we provide applications for download, you may download a single copy to your device solely for your own personal, non-commercial use.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.4 Trademarks</h3>
                <p className="text-gray-700">
                  The Company name, the Company logo, and all related names, logos, product and service names, designs, and slogans are trademarks of the Company or its affiliates or licensors. You must not use such marks without the prior written permission of the Company.
                </p>
              </section>

              {/* Section 7: Information on the Website */}
              <section id="information" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">7. INFORMATION ON THE WEBSITE</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.1 Accuracy of Information</h3>
                <p className="text-gray-700 mb-3">
                  We endeavour to ensure that the information on the Website is accurate and up to date. However, we do not warrant or represent that:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4 mb-4">
                  <li>(a) the information on the Website is accurate, complete, or current;</li>
                  <li>(b) the Website will be available at all times or at any given time;</li>
                  <li>(c) the Website will be free from errors, defects, malware, or viruses.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.2 Changes to Information</h3>
                <p className="text-gray-700 mb-4">
                  We reserve the right to change, modify, substitute, suspend, or remove without notice any information on the Website from time to time. We shall not be liable to you or any third party for any such changes.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.3 Not Professional Advice</h3>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                  <p className="text-gray-700 mb-3">
                    <strong className="text-amber-800">Important Notice:</strong> The information provided on the Website is for general informational purposes only. It does not constitute professional accounting, auditing, tax, legal, or financial advice. You should not act or refrain from acting on the basis of any information contained on the Website without seeking appropriate professional advice.
                  </p>
                  <p className="text-gray-700">
                    The content on this Website is not intended to create, and receipt of it does not constitute, an accountant to client relationship between you and the Company. No professional relationship is established by your use of this Website.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.4 No Reliance</h3>
                <p className="text-gray-700">
                  Any reliance you place on information from the Website is strictly at your own risk. We disclaim all liability and responsibility arising from any reliance placed on such materials by you or any other visitor to the Website, or by anyone who may be informed of any of its contents.
                </p>
              </section>

              {/* Section 8: User Contributions */}
              <section id="user-contributions" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">8. USER CONTRIBUTIONS</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">8.1 Types of Contributions</h3>
                <p className="text-gray-700 mb-4">
                  The Website may contain message boards, chat rooms, personal web pages or profiles, forums, bulletin boards, and other interactive features (collectively, "Interactive Services") that allow users to post, submit, publish, display, or transmit to other users or other persons (hereinafter, "post") content or materials (collectively, "User Contributions") on or through the Website.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">8.2 Standards for User Contributions</h3>
                <p className="text-gray-700 mb-3">
                  All User Contributions must comply with these Terms of Use. User Contributions must not:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent, hateful, inflammatory, or otherwise objectionable;</li>
                  <li>(b) promote sexually explicit or pornographic material, violence, or discrimination;</li>
                  <li>(c) infringe any intellectual property or other rights of any other person;</li>
                  <li>(d) violate the legal rights of others or contain any material that could give rise to civil or criminal liability;</li>
                  <li>(e) be likely to deceive any person;</li>
                  <li>(f) promote any illegal activity;</li>
                  <li>(g) cause annoyance, inconvenience, or needless anxiety;</li>
                  <li>(h) impersonate any person, or misrepresent your identity or affiliation;</li>
                  <li>(i) involve commercial activities or sales;</li>
                  <li>(j) give the impression that they emanate from or are endorsed by us if this is not the case.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">8.3 Rights in User Contributions</h3>
                <p className="text-gray-700 mb-3">
                  By posting User Contributions on the Website, you grant the Company a perpetual, irrevocable, worldwide, non-exclusive, royalty-free, sublicensable, and transferable licence to use, reproduce, distribute, prepare derivative works of, display, and perform the User Contributions in connection with the Website and the Company's business.
                </p>
                <p className="text-gray-700 mb-3">You represent and warrant that:</p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) you own or control all rights in and to the User Contributions;</li>
                  <li>(b) all of your User Contributions comply with these Terms of Use.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">8.4 Monitoring and Enforcement</h3>
                <p className="text-gray-700 mb-3">We have the right to:</p>
                <ul className="list-none space-y-2 text-gray-700 ml-4 mb-4">
                  <li>(a) remove or refuse to post any User Contributions for any or no reason;</li>
                  <li>(b) take any action with respect to any User Contribution that we deem necessary or appropriate;</li>
                  <li>(c) disclose your identity to any third party who claims that material posted by you violates their rights;</li>
                  <li>(d) take appropriate legal action for any illegal or unauthorised use of the Website;</li>
                  <li>(e) terminate or suspend your access to the Website for any violation of these Terms of Use.</li>
                </ul>
                <p className="text-gray-700">
                  We do not undertake to review all material before it is posted on the Website and cannot ensure prompt removal of objectionable material after it has been posted. Accordingly, we assume no liability for any action or inaction regarding transmissions, communications, or content provided by any user or third party.
                </p>
              </section>

              {/* Section 9: Links to Third Party Websites */}
              <section id="third-party-links" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">9. LINKS TO THIRD PARTY WEBSITES</h2>
                <p className="text-gray-700 mb-4">
                  The Website may contain links to third party websites or services that are not owned or controlled by the Company. The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party websites or services.
                </p>
                <p className="text-gray-700 mb-4">
                  We do not warrant the offerings of any of these entities, individuals, or their websites. You acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third party websites or services.
                </p>
                <p className="text-gray-700">
                  We strongly advise you to read the terms and conditions and privacy policies of any third party websites or services that you visit.
                </p>
              </section>

              {/* Section 10: Limitation of Liability */}
              <section id="limitation-liability" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">10. LIMITATION OF LIABILITY</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">10.1 Disclaimer of Warranties</h3>
                <p className="text-gray-700 mb-3">
                  You understand and agree that your use of the Website is at your sole risk. The Website is provided on an "as is" and "as available" basis. The Company expressly disclaims all warranties of any kind, whether express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </p>
                <p className="text-gray-700 mb-3">The Company makes no warranty that:</p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) the Website will meet your requirements;</li>
                  <li>(b) the Website will be available on an uninterrupted, timely, secure, or error-free basis;</li>
                  <li>(c) the results obtained from the use of the Website will be accurate or reliable;</li>
                  <li>(d) the quality of any products, services, or information obtained through the Website will meet your expectations.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">10.2 Limitation of Liability</h3>
                <p className="text-gray-700 mb-3">
                  To the fullest extent permitted by applicable law, in no event shall the Company, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4 mb-4">
                  <li>(a) your access to or use of or inability to access or use the Website;</li>
                  <li>(b) any conduct or content of any third party on the Website;</li>
                  <li>(c) any content obtained from the Website;</li>
                  <li>(d) unauthorised access, use, or alteration of your transmissions or content;</li>
                  <li>(e) any other matter relating to the Website.</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  This limitation applies whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, even if the Company has been advised of the possibility of such damage.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">10.3 Maximum Liability</h3>
                <p className="text-gray-700 mb-4">
                  In no event shall the Company's total liability to you for all damages, losses, and causes of action exceed the amount paid by you, if any, for accessing the Website, or One Thousand South African Rand (ZAR 1,000.00), whichever is greater.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">10.4 Applicable Law</h3>
                <p className="text-gray-700">
                  Some jurisdictions do not allow the exclusion of certain warranties or the limitation or exclusion of liability for incidental or consequential damages. Accordingly, some of the above limitations may not apply to you. In such jurisdictions, our liability will be limited to the greatest extent permitted by law.
                </p>
              </section>

              {/* Section 11: Indemnification */}
              <section id="indemnification" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">11. INDEMNIFICATION</h2>
                <p className="text-gray-700 mb-3">
                  You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable legal fees) arising out of or relating to:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) your violation of these Terms of Use;</li>
                  <li>(b) your use of the Website;</li>
                  <li>(c) your violation of any rights of another person or entity;</li>
                  <li>(d) your User Contributions.</li>
                </ul>
              </section>

              {/* Section 12: Governing Law and Jurisdiction */}
              <section id="governing-law" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">12. GOVERNING LAW AND JURISDICTION</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">12.1 Governing Law</h3>
                <p className="text-gray-700 mb-4">
                  These Terms of Use and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of the Republic of South Africa.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">12.2 Jurisdiction</h3>
                <p className="text-gray-700">
                  You irrevocably agree that the courts of the Republic of South Africa shall have exclusive jurisdiction to settle any dispute or claim that arises out of or in connection with these Terms of Use or their subject matter or formation (including non-contractual disputes or claims).
                </p>
              </section>

              {/* Section 13: Dispute Resolution */}
              <section id="dispute-resolution" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">13. DISPUTE RESOLUTION</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">13.1 Negotiation</h3>
                <p className="text-gray-700 mb-4">
                  In the event of any dispute, controversy, or claim arising out of or relating to these Terms of Use, or the breach, termination, or invalidity thereof (a "Dispute"), the parties shall first attempt to resolve the Dispute through good faith negotiations.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">13.2 Mediation</h3>
                <p className="text-gray-700">
                  If the Dispute cannot be resolved through negotiation within thirty (30) days, the parties agree to attempt to resolve the Dispute through mediation administered by the Arbitration Foundation of Southern Africa (AFSA) or another mutually agreed upon mediator, before resorting to litigation.
                </p>
              </section>

              {/* Section 14: Termination */}
              <section id="termination" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">14. TERMINATION</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">14.1 Termination by Company</h3>
                <p className="text-gray-700 mb-4">
                  We may terminate or suspend your access to the Website immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms of Use.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">14.2 Effect of Termination</h3>
                <p className="text-gray-700 mb-4">
                  Upon termination, your right to use the Website will immediately cease. All provisions of these Terms of Use which by their nature should survive termination shall survive termination, including without limitation ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">14.3 Termination by User</h3>
                <p className="text-gray-700">
                  If you wish to terminate your use of the Website, you may simply discontinue using the Website.
                </p>
              </section>

              {/* Section 15: Waiver and Severability */}
              <section id="waiver-severability" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">15. WAIVER AND SEVERABILITY</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">15.1 Waiver</h3>
                <p className="text-gray-700 mb-4">
                  No waiver by the Company of any term or condition set forth in these Terms of Use shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure of the Company to assert a right or provision under these Terms of Use shall not constitute a waiver of such right or provision.
                </p>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">15.2 Severability</h3>
                <p className="text-gray-700">
                  If any provision of these Terms of Use is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of these Terms of Use will continue in full force and effect.
                </p>
              </section>

              {/* Section 16: Entire Agreement */}
              <section id="entire-agreement" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">16. ENTIRE AGREEMENT</h2>
                <p className="text-gray-700">
                  These Terms of Use, together with our Privacy Policy, constitute the sole and entire agreement between you and OMC Chartered Accountants (Pty) Ltd with respect to the Website and supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral, with respect to the Website.
                </p>
              </section>

              {/* Section 17: Electronic Communications */}
              <section id="electronic-communications" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">17. ELECTRONIC COMMUNICATIONS</h2>
                <p className="text-gray-700">
                  By using the Website, you consent to receiving electronic communications from us. These electronic communications may include notices about applicable fees and charges, transactional information, and other information concerning or related to the Website. You agree that any notices, agreements, disclosures, or other communications that we send to you electronically will satisfy any legal communication requirements, including that such communications be in writing.
                </p>
              </section>

              {/* Section 18: Assignment */}
              <section id="assignment" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">18. ASSIGNMENT</h2>
                <p className="text-gray-700">
                  You may not assign or transfer these Terms of Use, by operation of law or otherwise, without our prior written consent. Any attempt by you to assign or transfer these Terms of Use without such consent will be null and void. We may freely assign or transfer these Terms of Use without restriction. Subject to the foregoing, these Terms of Use will bind and inure to the benefit of the parties, their successors, and permitted assigns.
                </p>
              </section>

              {/* Section 19: Force Majeure */}
              <section id="force-majeure" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">19. FORCE MAJEURE</h2>
                <p className="text-gray-700">
                  The Company shall not be liable for any failure or delay in performance under these Terms of Use due to causes beyond its reasonable control, including but not limited to acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of transportation facilities, fuel, energy, labour, or materials.
                </p>
              </section>

              {/* Section 20: Notices */}
              <section id="notices" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">20. NOTICES</h2>
                
                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">20.1 Notices to Company</h3>
                <p className="text-gray-700 mb-3">
                  All notices, requests, consents, claims, demands, waivers, and other communications to the Company under these Terms of Use must be in writing and addressed to:
                </p>
                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <p className="text-gray-700 font-semibold mb-2">OMC Chartered Accountants (Pty) Ltd</p>
                  <p className="text-gray-700">3 North Close</p>
                  <p className="text-gray-700">Sunninghill Estate</p>
                  <p className="text-gray-700 mb-3">2191, South Africa</p>
                  <p className="text-gray-700">Email: <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a></p>
                  <p className="text-gray-700">Telephone: <a href="tel:+27100011234" className="text-[#0383ED] hover:underline">+27 10 001 1234</a></p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">20.2 Notices to Users</h3>
                <p className="text-gray-700">
                  We may provide notices to you via email to the email address you provide to us, by posting a notice on the Website, or by any other method we choose. You are responsible for ensuring that your contact information is current and accurate.
                </p>
              </section>

              {/* Section 21: Accessibility */}
              <section id="accessibility" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">21. ACCESSIBILITY</h2>
                <p className="text-gray-700">
                  We are committed to making our Website accessible to all users. If you experience any difficulty in accessing any part of this Website, please contact us at <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a>, and we will work with you to provide the information you seek through an alternate communication method.
                </p>
              </section>

              {/* Section 22: Language */}
              <section id="language" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">22. LANGUAGE</h2>
                <p className="text-gray-700">
                  These Terms of Use are drafted in English. In the event that these Terms of Use are translated into any other language, the English version shall prevail to the extent of any inconsistency.
                </p>
              </section>

              {/* Section 23: Questions and Contact Information */}
              <section id="contact" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">23. QUESTIONS AND CONTACT INFORMATION</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions, comments, or concerns regarding these Terms of Use, please contact us at:
                </p>
                <div className="bg-gradient-to-br from-[#000565] to-[#0383ED] text-white rounded-lg p-6">
                  <p className="font-semibold mb-2">OMC Chartered Accountants (Pty) Ltd</p>
                  <p className="mb-1">3 North Close</p>
                  <p className="mb-1">Sunninghill Estate</p>
                  <p className="mb-3">2191, South Africa</p>
                  <p className="mb-1">Email: <a href="mailto:admin@omcca.co.za" className="text-white hover:text-blue-200 underline">admin@omcca.co.za</a></p>
                  <p>Telephone: <a href="tel:+27100011234" className="text-white hover:text-blue-200 underline">+27 10 001 1234</a></p>
                </div>
              </section>

              {/* Bottom Notice */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-6">
                  <Shield className="w-6 h-6 text-[#0383ED] flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong className="text-[#000565]">Your Continued Use:</strong> By continuing to access or use our Website after any revisions become effective, you agree to be bound by the revised terms.
                    </p>
                    <p className="text-gray-700">
                      If you do not agree to the new terms, please stop using the Website. For questions about these Terms of Use, please <Link to="/contact" className="text-[#0383ED] hover:underline font-medium">contact us</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;